import { useEffect, useRef, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import TechnicianService from "src/Services/API/technician.service";
import { can, handleError, isAccountant, isManagerOver } from "src/Common/function";
import { vocabularize } from "src/Common/vocab";
import TimeInput, {
  EMPTY_TIME,
  isNormalizedTimeValue,
  normalizeTimeValue,
  parseUserTime,
} from "src/Components/Common/timeinput";

const DayTechItem = ({
  tech,
  currentDate,
  startDateString,
  timesheetUpdated,
  activeEditingTechId,
  setActiveEditingTechId,
  woStatus,
}) => {
  const [techData, setTechData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Raw strings the user is currently typing. Only normalized on commit.
  const [editedTimes, setEditedTimes] = useState({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  // Snapshot of normalized times at the moment editing started. Used as the
  // diff baseline on save so we don't send unchanged fields to the API.
  const baselineTimesRef = useRef({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  const isEditing = activeEditingTechId === tech?.id;
  const isFutureStartDate = moment(startDateString).isAfter(moment(), "day");

  // technician.status: 0 = requested, 1 = accepted, 2 = rejected. Hours can
  // only be logged for a technician who has accepted the WO; otherwise the edit
  // button just explains why.
  const notAcceptedMessage =
    tech?.status === 0
      ? `${tech.user?.name} ${vocabularize("hasn't accepted this work order yet. Hours can be added once they accept it.")}`
      : tech?.status === 2
      ? `${tech.user?.name} ${vocabularize("rejected this work order, so hours can't be added for them.")}`
      : null;

  useEffect(() => {
    let technician = _.cloneDeep(tech);
    if (tech?.timesheet) {
      for (let i = 0; i < tech.timesheet.length; i++) {
        const workDate = moment(startDateString).add(Number(tech.timesheet[i].dayDiff || 0), "days");
        const isSameDate = workDate.isSame(currentDate, "day");

        if (isSameDate) {
          technician.regularTime = tech.timesheet[i].regularTime;
          technician.travelTime = tech.timesheet[i].travelTime;
          technician.overTime = tech.timesheet[i].overTime;
        }
      }
    }

    setTechData(technician);
    const normalized = {
      regularTime: normalizeTimeValue(technician.regularTime),
      travelTime: normalizeTimeValue(technician.travelTime),
      overTime: normalizeTimeValue(technician.overTime),
    };
    setEditedTimes(normalized);
    baselineTimesRef.current = normalized;
  }, [tech, currentDate, startDateString]);

  const handleFieldKeyDown = (fieldName, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Parse all three fields synchronously so we don't race React state.
      const finalTimes = {
        regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
        travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
        overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
      };
      setEditedTimes(finalTimes);
      updateTimesheet(finalTimes);
      setActiveEditingTechId(null);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Revert this field to its baseline and exit edit mode.
      setEditedTimes((previous) => ({
        ...previous,
        [fieldName]: baselineTimesRef.current[fieldName],
      }));
      setActiveEditingTechId(null);
    }
  };

  const renderTimeField = (fieldName) => {
    if (!isEditing) {
      const value = techData?.[fieldName];
      return (
        <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
          {value && value !== EMPTY_TIME ? value : "__:__"}
        </p>
      );
    }

    return (
      <TimeInput
        value={editedTimes[fieldName]}
        onChange={(next) => setEditedTimes((previous) => ({ ...previous, [fieldName]: next }))}
        onKeyDown={(e) => handleFieldKeyDown(fieldName, e)}
        className="w-[97px] h-[34px] text-center rounded-[10px] border-[1px] border-borderColor bg-white font-OpenSansRegular text-[14px] text-titleColor outline-none"
      />
    );
  };

  const onEditButtonClick = () => {
    if (!isEditing) {
      if (activeEditingTechId && activeEditingTechId !== tech.id) {
        return;
      }
      // Snapshot the baseline at the moment we enter edit mode.
      baselineTimesRef.current = {
        regularTime: normalizeTimeValue(techData?.regularTime),
        travelTime: normalizeTimeValue(techData?.travelTime),
        overTime: normalizeTimeValue(techData?.overTime),
      };
      setEditedTimes(baselineTimesRef.current);
      setActiveEditingTechId(tech.id);
      return;
    }

    // Normalize all three fields before saving in case the user clicked the
    // check mark without blurring the input first.
    const finalTimes = {
      regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
      travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
      overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
    };
    setEditedTimes(finalTimes);
    updateTimesheet(finalTimes);
    setActiveEditingTechId(null);
  };

  const updateTimesheet = (overrideTimes) => {
    if (isSubmitting) {
      return;
    }
    const finalTimes = overrideTimes || editedTimes;
    const originalTimes = baselineTimesRef.current;

    let changedTimes = {};
    if (finalTimes.regularTime !== originalTimes.regularTime && isNormalizedTimeValue(finalTimes.regularTime)) {
      changedTimes.regularTime = finalTimes.regularTime;
    }
    if (finalTimes.travelTime !== originalTimes.travelTime && isNormalizedTimeValue(finalTimes.travelTime)) {
      changedTimes.travelTime = finalTimes.travelTime;
    }
    if (finalTimes.overTime !== originalTimes.overTime && isNormalizedTimeValue(finalTimes.overTime)) {
      changedTimes.overTime = finalTimes.overTime;
    }

    if (Object.keys(changedTimes).length === 0) {
      return;
    }

    const dayDiff = moment(currentDate).diff(moment(startDateString), "days");

    let timesheet = [];
    if (techData.timesheet && techData.timesheet.length > 0) {
      timesheet = _.cloneDeep(techData.timesheet);
      if (timesheet) {
        let isFound = false;
        for (let i = 0; i < timesheet.length; i++) {
          if (timesheet[i].dayDiff === dayDiff) {
            if (changedTimes.regularTime) {
              timesheet[i].regularTime = changedTimes.regularTime;
            }
            if (changedTimes.travelTime) {
              timesheet[i].travelTime = changedTimes.travelTime;
            }
            if (changedTimes.overTime) {
              timesheet[i].overTime = changedTimes.overTime;
            }
            isFound = true;
          }
        }
        if (!isFound) {
          let temp = _.cloneDeep(changedTimes);
          temp.dayDiff = dayDiff;
          timesheet.push(temp);
        }
      }
    } else {
      let temp = _.cloneDeep(changedTimes);
      temp.dayDiff = dayDiff;
      timesheet.push(temp);
    }

    setIsSubmitting(true);
    TechnicianService.updateTimesheet({
      id: techData.id,
      data: { timesheet: JSON.stringify(timesheet) },
    })
      .then((response) => {
        toast("Timesheet updated successfully");
        timesheetUpdated();
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    techData && (
      <div className="w-full h-[40px] flex items-center px-3 border-t-[1px] border-borderColor relative">
        <img src={getAvatarUrl(techData.user.avatar)} className="w-[24px] rounded-[12px]" />
        <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor ml-2 flex-1 min-w-0 truncate mr-[calc(5%+325px)]">
          {techData.user.name}
        </p>

        <div className="absolute right-[5%] top-[3px] h-[34px] flex items-center gap-3">
          {renderTimeField("regularTime")}
          {renderTimeField("travelTime")}
          {renderTimeField("overTime")}
        </div>

        {/* Timesheet save always hits /update_timesheet_staff (wo:logTimesheet-gated),
          so logging on a billed WO needs wo:logTimesheet AND the extra
          wo:logBilledTimesheet — the latter unlocks editing once billed. */}
        {!isFutureStartDate &&
          can("wo:logTimesheet") &&
          (woStatus < 100 || (woStatus === 100 && can("wo:logBilledTimesheet"))) && (
            <div className="absolute right-[1%] top-[3px]">
              {notAcceptedMessage ? (
                <NotAcceptedEditButton tooltipId={`timesheet-not-accepted-${tech.id}`} message={notAcceptedMessage} />
              ) : (
                <EditButton
                  clicked={onEditButtonClick}
                  isEditing={isEditing}
                  isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
                />
              )}
            </div>
          )}
      </div>
    )
  );
};

export default DayTechItem;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  // When editing, swallow mousedown so the focused input doesn't blur before
  // our click handler runs. Blur would fire commitField and race with the
  // save path, leaving us with stale state.
  const handleMouseDown = (event) => {
    if (isEditing) {
      event.preventDefault();
    }
  };

  return isEditing ? (
    <div
      onMouseDown={handleMouseDown}
      onClick={clicked}
      className="w-[18px] absolute right-[5%] top-[12px] cursor-pointer"
    >
      <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
    </div>
  ) : (
    <div
      onClick={isDisabled ? undefined : clicked}
      className={`w-[18px] absolute right-[5%] top-[12px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
    </div>
  );
};

// Dimmed edit button for a technician who hasn't accepted the WO. Hovering or
// clicking it pops up the reason instead of opening the time inputs.
const NotAcceptedEditButton = ({ tooltipId, message }) => (
  <>
    <div
      data-tooltip-id={tooltipId}
      data-tooltip-content={message}
      className="w-[18px] absolute right-[5%] top-[12px] cursor-not-allowed opacity-40"
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" alt="" />
    </div>
    <Tooltip
      id={tooltipId}
      place="left"
      positionStrategy="fixed"
      openEvents={{ mouseenter: true, click: true }}
      closeEvents={{ mouseleave: true }}
      globalCloseEvents={{ clickOutsideAnchor: true, escape: true, scroll: true }}
      className="!max-w-[260px] !rounded-[8px] !bg-[#1A2332] !px-3 !py-2 !text-[11px] !font-OpenSansRegular !leading-[16px] z-50"
    />
  </>
);
